import React from 'react';
import type { Member } from '../../types/member';
import type { FinancialHealthStats } from '../../types/financialStats';

interface ScoreComparisonCardProps {
  member: Member;
  stats: FinancialHealthStats[];
  getCurrentValue: <K extends keyof FinancialHealthStats>(
    stats: FinancialHealthStats[],
    key: K,
    defaultValue?: number
  ) => number;
  // Accept optional overrideCreditScore so the card displays the same
  // authoritative value as the rest of the dashboard (member.credit_score
  // from credit_scores.score or Demo2 synthetic overlay).
  getMockCreditScore: (
    stats: FinancialHealthStats[],
    overrideCreditScore?: number | null
  ) => number;
  hasUnlockedCreditOpportunities: (
    stats: FinancialHealthStats[],
    overrideCreditScore?: number | null
  ) => boolean;
}

const ScoreComparisonCard: React.FC<ScoreComparisonCardProps> = ({
  member,
  stats,
  getCurrentValue,
  getMockCreditScore,
  hasUnlockedCreditOpportunities
}) => {
  const veraScore = getCurrentValue(stats, 'verascore', 0);
  const creditScore = getMockCreditScore(stats, member.credit_score);

  return (
    <div className="p-4 rounded-lg mb-6" style={{
      background: 'linear-gradient(to bottom, rgba(209, 250, 229, 0.2), rgba(167, 243, 208, 0.2))',
      border: '1px solid rgba(16, 185, 129, 0.3)',
      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.05)'
    }}>
      <h4 className="font-medium text-slate-700 mb-3 flex items-center">
        <span 
          className="w-6 h-6 flex items-center justify-center rounded-full mr-2"
          style={{
            background: 'linear-gradient(to bottom right, #10b981, #059669)',
            boxShadow: '0 1px 2px rgba(5, 150, 105, 0.3)'
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-white" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
          </svg>
        </span>
        <span className="text-lg">VeraScore vs. Credit Score Comparison</span>
      </h4>
      
      <div className="flex mb-3">
        {/* VeraScore Section */}
        <div className="flex-1 p-3 rounded-lg mr-3" style={{
          background: 'rgba(255, 255, 255, 0.8)',
          border: '1px solid rgba(16, 185, 129, 0.2)',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
        }}>
          <div className="text-sm text-slate-600 mb-1 font-medium">VeraScore</div>
          <div className="text-2xl font-semibold text-slate-700">
            {veraScore.toFixed(0)} 
            <span className="text-sm font-normal text-slate-500 ml-1">/100</span>
          </div>
          <div className={`text-sm mt-1 ${veraScore >= 60 ? 'text-emerald-600' : veraScore >= 40 ? 'text-amber-600' : 'text-rose-600'}`}>
            {veraScore >= 80 ? 'Excellent' : 
             veraScore >= 60 ? 'Good' : 
             veraScore >= 40 ? 'Fair' : 'Poor'}
          </div>
        </div>
        
        {/* Credit Score Section */}
        <div className="flex-1 p-3 rounded-lg" style={{
          background: 'rgba(255, 255, 255, 0.8)',
          border: '1px solid rgba(209, 213, 219, 0.3)',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
        }}>
          <div className="text-sm text-slate-600 mb-1 font-medium">Credit Score</div>
          <div className="text-2xl font-semibold text-slate-700">
            {creditScore} 
            <span className="text-sm font-normal text-slate-500 ml-1">/850</span>
          </div>
          <div className={`text-sm mt-1 ${creditScore >= 700 ? 'text-emerald-600' : creditScore >= 650 ? 'text-amber-600' : 'text-rose-600'}`}>
            {creditScore >= 750 ? 'Excellent' : 
             creditScore >= 700 ? 'Good' : 
             creditScore >= 650 ? 'Fair' : 
             creditScore >= 600 ? 'Poor' : 'Very Poor'}
          </div>
        </div>
      </div>
      
      {/* Discrepancy Indicator */}
      {hasUnlockedCreditOpportunities(stats, member.credit_score) ? (
        <div className="p-3 rounded-lg mt-3" style={{
          backgroundColor: 'rgba(16, 185, 129, 0.15)',
          border: '1px solid rgba(16, 185, 129, 0.3)',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
        }}>
          <div className="flex items-start">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0 text-emerald-700" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="font-bold text-emerald-800 mb-1">CREDIT OPPORTUNITY</p>
              <p className="text-slate-700">
                {member.first_name}'s traditional credit score would typically limit lending options, but their strong VeraScore reveals better financial health and qualifies them for additional credit products based on real-time financial behavior.
              </p>
            </div>
          </div>
        </div>
      ) : (
        (veraScore >= 50 && creditScore < 630) ? (
          <div className="p-3 rounded-lg mt-3" style={{
            backgroundColor: 'rgba(16, 185, 129, 0.15)',
            border: '1px solid rgba(16, 185, 129, 0.3)',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
          }}>
            <div className="flex items-start">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0 text-emerald-700" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="font-bold text-emerald-800 mb-1">OPPORTUNITY INSIGHT</p>
                <p className="text-slate-700">
                  While {member.first_name}'s credit score shows risks, their VeraScore indicates better financial management in recent months, suggesting growth potential.
                </p>
              </div>
            </div>
          </div>
        ) : (creditScore >= 680 && veraScore < 45) ? (
          <div className="p-3 rounded-lg mt-3" style={{
            background: 'rgba(254, 202, 202, 0.2)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
          }}>
            <div className="flex items-start">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0 text-rose-600" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="font-bold text-rose-700 mb-1">RISK ALERT</p>
                <p className="text-slate-700">
                  Despite a good credit score, {member.first_name}'s VeraScore reveals current financial struggles that may indicate emerging risk factors not yet reflected in traditional credit data.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-3 rounded-lg mt-3" style={{
            background: 'rgba(187, 247, 208, 0.2)',
            border: '1px solid rgba(74, 222, 128, 0.3)',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
          }}>
            <div className="flex items-start">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0 text-green-600" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="font-bold text-green-700 mb-1">CONSISTENT ASSESSMENT</p>
                <p className="text-slate-700">
                  {member.first_name}'s credit score and VeraScore provide aligned risk assessments, confirming their current financial position from multiple perspectives.
                </p>
              </div>
            </div>
          </div>
        )
      )}
    </div>
  );
};

export default ScoreComparisonCard;